import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"
import {vec3} from "#material/types/vector"
import type {DataType, InterpolationType} from "#material/nodes/converter/map-range/helpers"

export interface MapRangeSockets extends MaterialNodeSockets {
    inputs: {
        fromMax: "float" // found in inputs: -; found in settings: float; inferred from data: float
        fromMin: "float" // found in inputs: -; found in settings: float; inferred from data: float
        result: "float" // found in inputs: -; found in settings: float; inferred from data: float
        steps: "float" // found in inputs: -; found in settings: float; inferred from data: float
        toMax: "float" // found in inputs: -; found in settings: float; inferred from data: float
        toMin: "float" // found in inputs: -; found in settings: float; inferred from data: float
        value: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
    }
    settings: {
        clamp: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        fromMax: "float" // found in inputs: -; found in settings: float; inferred from data: float
        fromMaxFloat3: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        fromMin: "float" // found in inputs: -; found in settings: float; inferred from data: float
        fromMinFloat3: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        result: "float" // found in inputs: -; found in settings: float; inferred from data: float
        steps: "float" // found in inputs: -; found in settings: float; inferred from data: float
        stepsFloat3: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        toMax: "float" // found in inputs: -; found in settings: float; inferred from data: float
        toMaxFloat3: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        toMin: "float" // found in inputs: -; found in settings: float; inferred from data: float
        toMinFloat3: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        value: "float" // found in inputs: color,float; found in settings: float; inferred from data: float
        vector: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
    }
    settingsExtensions: {
        dataType?: DataType
        interpolationType?: InterpolationType // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        result: "float"
    }
}

export const mapRangeDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeMapRange,
    },
    nodeClass: "MapRange",
    ui: {
        label: "Map Range",
        color: "#4987af",
        items: [
            {name: "result", valueType: "float", label: "Result", output: true},
            {name: "value", valueType: "float", label: "Value", input: true, setting: true, defaultValue: 1},
            {name: "fromMin", valueType: "float", label: "From Min", input: true, setting: true, defaultValue: 0},
            {name: "fromMax", valueType: "float", label: "From Max", input: true, setting: true, defaultValue: 1},
            {name: "toMin", valueType: "float", label: "To Min", input: true, setting: true, defaultValue: 0},
            {name: "toMax", valueType: "float", label: "To Max", input: true, setting: true, defaultValue: 1},
            // {name: "steps", valueType: "float", label: "Steps", input: true, setting: true, defaultValue: 4},
            {
                name: "interpolationType",
                defaultValue: "LINEAR",
                label: "Interpolation",
                valueType: "string",
                options: [
                    {label: "Linear", value: "LINEAR"},
                    {label: "Stepped", value: "STEPPED"},
                    {label: "Smoothstep", value: "SMOOTHSTEP"},
                    {label: "Smootherstep", value: "SMOOTHERSTEP"},
                ],
                input: false,
                setting: true,
            },
            {name: "clamp", label: "Clamp", valueType: "boolean", defaultValue: false, input: false, setting: true},
        ],
        support: {
            cycles: true,
            three: {full: false, remark: "Only linear interpolation, some parameters not supported."},
        },
    },
    validation: {
        returns: z.strictObject({
            result: materialFloatOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({
                value: optionalMaterialInput,
                fromMin: optionalMaterialInput,
                fromMax: optionalMaterialInput,
                toMin: optionalMaterialInput,
                toMax: optionalMaterialInput,
                steps: optionalMaterialInput,
                result: optionalMaterialInput,
            }),
            settings: z.strictObject({
                clamp: z.optional(z.boolean()),
                dataType: z.optional(z.enum(["FLOAT"])),
                fromMax: z.optional(z.number()),
                fromMaxFloat3: z.optional(vec3),
                fromMin: z.optional(z.number()),
                fromMinFloat3: z.optional(vec3),
                interpolationType: z.optional(z.enum(["LINEAR", "STEPPED", "SMOOTHSTEP", "SMOOTHERSTEP"])),
                result: z.optional(z.number()),
                steps: z.optional(z.number()),
                stepsFloat3: z.optional(vec3),
                toMax: z.optional(z.number()),
                toMaxFloat3: z.optional(vec3),
                toMin: z.optional(z.number()),
                toMinFloat3: z.optional(vec3),
                value: z.optional(z.number()),
                vector: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<MapRangeSockets>
